import { useState, useEffect, useCallback } from 'react';
import { arrayMove } from '@dnd-kit/sortable';
import { CONTAINER_IDS, ADD_GEARBOX_DEFAULTS } from '../constants';

// Allowed asset types for physical connection
const ALLOWED_ASSET_TYPES = ['Motor', 'Drive', 'GenericMachine', 'DE_Pump'];

/**
 * Checks if an asset type is allowed in physical connection
 */
const isAssetTypeAllowed = (assetType) => {
    if (!assetType) {
        return false;
    }
    return ALLOWED_ASSET_TYPES.some(allowed =>
        assetType.toLowerCase() === allowed.toLowerCase()
    );
};

/**
 * Custom hook for managing asset state and operations
 * Handles selected assets and physical connection sequence
 * Follows React best practices: immutability, controlled state, immediate sync
 */
export const useAssetManagement = (initialStepData = {}, onStepDataChange) => {
    const [selectedAssets, setSelectedAssets] = useState(initialStepData.selectedAssets || []);
    const [physicalConnection, setPhysicalConnection] = useState(
        initialStepData.physicalConnection || []
    );

    // IMMEDIATE SYNCHRONIZATION: Sync to parent whenever assets change
    const syncToParent = useCallback((newAssets, newConnection) => {
        // Build outputRatios object from both selected assets and physical connection
        const outputRatios = {};

        // First, collect ratios from selected assets
        newAssets.forEach(asset => {
            if (asset?.outputSpeedRatio !== undefined && asset?.outputSpeedRatio !== null) {
                outputRatios[asset?.assetId] = String(asset?.outputSpeedRatio);
            }
        });

        // Then, override with ratios from physical connection (these take precedence)
        newConnection.forEach(asset => {
            if (asset?.outputSpeedRatio !== undefined && asset?.outputSpeedRatio !== null) {
                outputRatios[asset?.assetId] = String(asset?.outputSpeedRatio);
            }
        });

        onStepDataChange({
            selectedAssets: newAssets,
            physicalConnection: newConnection,
            outputRatios: outputRatios
        });
    }, [onStepDataChange]);

    // CONTROLLED COMPONENT: Update local state when parent data changes
    // This ensures Step 3 always has fresh data when returning from Step 2
    useEffect(() => {
        const incomingAssets = initialStepData.selectedAssets || [];
        const incomingConnection = initialStepData.physicalConnection || [];
        // const version = initialStepData._version || 'unknown';

        // FIX: Compare by asset IDs (deep comparison) instead of array reference
        const incomingAssetIds = new Set(incomingAssets.map(a => a.id));
        const currentAssetIds = new Set(selectedAssets.map(a => a.id));

        // Check if assets have actually changed (new additions or removals)
        const hasNewAssets = incomingAssets.some(a => !currentAssetIds.has(a.id));
        const hasRemovedAssets = selectedAssets.some(a => !incomingAssetIds.has(a.id));
        const hasCountChanged = incomingAssets?.length !== selectedAssets?.length;

        // Check if asset properties changed (like isConnectedToPowertrain)
        const hasPropertyChanges = incomingAssets.some(incomingAsset => {
            const currentAsset = selectedAssets.find(a => a.id === incomingAsset.id);
            if (!currentAsset) {
                return false;
            }
            return currentAsset.isConnectedToPowertrain !== incomingAsset.isConnectedToPowertrain;
        });

        // FIX: Always update if incoming data differs from current state
        if (hasNewAssets || hasRemovedAssets || hasCountChanged || hasPropertyChanges) {
            // IMMUTABLE UPDATE: Create new array
            setSelectedAssets([...incomingAssets]);

            // ENHANCEMENT: Clean up physical connection while preserving output ratios
            const cleanedConnection = incomingConnection
                .filter(asset => incomingAssetIds.has(asset.id))
                .map(asset => {
                    // Unmonitored gearboxes: preserve as-is – they have no matching regular asset
                    if (asset?.isUnmonitoredGearbox) {
                        return asset;
                    }
                    // Find the asset in incoming list to get latest data
                    const latestAsset = incomingAssets.find(a => a.id === asset.id);
                    if (latestAsset) {
                        // Preserve output ratio if it was configured (not default 0)
                        const preservedRatio = asset?.outputSpeedRatio !== undefined && asset?.outputSpeedRatio !== 0
                            ? asset.outputSpeedRatio
                            : latestAsset.outputSpeedRatio || 0;

                        return {
                            ...latestAsset,
                            outputSpeedRatio: preservedRatio
                        };
                    }
                    return asset;
                });

            if (cleanedConnection.length !== incomingConnection.length) {
                setPhysicalConnection(cleanedConnection);
                // Sync cleaned connection back to parent
                syncToParent(incomingAssets, cleanedConnection);
            } else {
                setPhysicalConnection([...incomingConnection]);
            }
        }
    }, [
        // React to version changes which include asset count changes
        initialStepData._version,
        selectedAssets.length, // Track internal state length
        syncToParent
    ]);

    // Get available assets (not in physical connection)
    const availableAssets = selectedAssets.filter(
        asset => !physicalConnection.find(conn => conn.id === asset.id)
    );

    // Helper function to find which container an item belongs to
    const findContainer = (id) => {
        if (availableAssets.find(asset => asset.id === id)) {
            return CONTAINER_IDS.SELECTED_ASSETS;
        }
        if (physicalConnection.find(asset => asset.id === id)) {
            return CONTAINER_IDS.PHYSICAL_CONNECTION;
        }
        return null;
    };

    // CALLBACK STABILITY: Memoized handlers with useCallback
    const moveToPhysicalConnection = useCallback((id) => {
        const asset = selectedAssets.find(a => a.id === id);
        if (asset && !physicalConnection.find(a => a.id === id)) {
            // Validate asset type before allowing drop
            const assetType = asset.assetType || asset.type;
            if (!isAssetTypeAllowed(assetType)) {
                return; // Prevent drop
            }

            // IMMUTABLE UPDATE: Create new array with the new asset
            // IMPORTANT: Preserve the outputSpeedRatio when moving
            const newPhysicalConnection = [...physicalConnection, {
                ...asset,
                outputSpeedRatio: asset.outputSpeedRatio || null // Explicitly preserve the ratio
            }];
            setPhysicalConnection(newPhysicalConnection);

            // IMMEDIATE SYNCHRONIZATION: Sync to parent
            // Keep selectedAssets unchanged - availableAssets is derived from the difference
            syncToParent(selectedAssets, newPhysicalConnection);
        }
    }, [selectedAssets, physicalConnection, syncToParent]);

    const removeFromPhysicalConnection = useCallback((id) => {
        const assetToRemove = physicalConnection.find(a => a.id === id);

        if (assetToRemove) {
            // IMMUTABLE UPDATE
            const newPhysicalConnection = physicalConnection.filter(asset => asset.id !== id);
            setPhysicalConnection(newPhysicalConnection);

            // IMMEDIATE SYNCHRONIZATION
            // Keep selectedAssets unchanged - availableAssets is derived from the difference
            syncToParent(selectedAssets, newPhysicalConnection);
        }
    }, [selectedAssets, physicalConnection, syncToParent]);

    const reorderPhysicalConnection = useCallback((activeId, overId) => {
        const oldIndex = physicalConnection.findIndex(asset => asset.id === activeId);
        const newIndex = physicalConnection.findIndex(asset => asset.id === overId);

        if (oldIndex !== newIndex && oldIndex !== -1 && newIndex !== -1) {
            // IMMUTABLE UPDATE: Create new reordered array
            const newPhysicalConnection = arrayMove(physicalConnection, oldIndex, newIndex);
            setPhysicalConnection(newPhysicalConnection);

            // IMMEDIATE SYNCHRONIZATION
            syncToParent(selectedAssets, newPhysicalConnection);
        }
    }, [selectedAssets, physicalConnection, syncToParent]);

    const updateAssetOutputRatio = useCallback((assetId, outputSpeedRatio) => {
        // Update in selectedAssets (for assets in available panel)
        const newSelectedAssets = selectedAssets.map(asset =>
            asset.assetId === assetId
                ? { ...asset, outputSpeedRatio }
                : asset
        );
        setSelectedAssets(newSelectedAssets);

        // Update in physicalConnection (for assets already in physical connection panel)
        const newPhysicalConnection = physicalConnection.map(asset =>
            asset.assetId === assetId
                ? { ...asset, outputSpeedRatio }
                : asset
        );
        setPhysicalConnection(newPhysicalConnection);

        // IMMEDIATE SYNCHRONIZATION
        syncToParent(newSelectedAssets, newPhysicalConnection);
    }, [selectedAssets, physicalConnection, syncToParent]);

    /**
     * Add an unmonitored gearbox to the physical connection.
     * Places it after the first motor in the connection, or at the end if no motor exists.
     */
    const addGearboxToConnection = useCallback((gearboxData) => {
        const tempId = `${ADD_GEARBOX_DEFAULTS.TEMP_ID_PREFIX}${Date.now()}`;
        const gearboxAsset = {
            id: `asset-${tempId}`,
            name: gearboxData.assetName,
            type: ADD_GEARBOX_DEFAULTS.ASSET_TYPE.toLowerCase(),
            assetId: tempId,
            selectedSubType: ADD_GEARBOX_DEFAULTS.SUB_TYPE,
            outputSpeedRatio: gearboxData.outputSpeedRatio || '',
            isUnmonitoredGearbox: true,
            isConnectedToPowertrain: false,
            isConnectedToSamePowertrain: false,
            connectedPowertrainName: null,
            gearboxMetadata: {
                assetAccessGroup: gearboxData.assetAccessGroup,
                organization: gearboxData.organization,
                site: gearboxData.site
            },
            originalAsset: {
                assetId: tempId,
                assetName: gearboxData.assetName,
                assetType: ADD_GEARBOX_DEFAULTS.ASSET_TYPE,
                selectedSubType: ADD_GEARBOX_DEFAULTS.SUB_TYPE,
                isUnmonitoredGearbox: true
            }
        };

        // Find the first motor in physical connection to place gearbox after it
        const motorIndex = physicalConnection.findIndex(asset => {
            const assetType = (asset.type || '').toLowerCase();
            return assetType === 'motor';
        });

        let newPhysicalConnection;
        if (motorIndex >= 0) {
            // Insert after the first motor
            newPhysicalConnection = [
                ...physicalConnection.slice(0, motorIndex + 1),
                gearboxAsset,
                ...physicalConnection.slice(motorIndex + 1)
            ];
        } else {
            // No motor found, add at the end
            newPhysicalConnection = [...physicalConnection, gearboxAsset];
        }

        // Also add to selectedAssets to keep it tracked
        const newSelectedAssets = [...selectedAssets, gearboxAsset];

        setSelectedAssets(newSelectedAssets);
        setPhysicalConnection(newPhysicalConnection);
        syncToParent(newSelectedAssets, newPhysicalConnection);

        return gearboxAsset;
    }, [selectedAssets, physicalConnection, syncToParent]);

    /**
     * Remove an unmonitored gearbox from the connection and selected assets.
     * Only removes gearboxes that were added via the dialog (have isUnmonitoredGearbox flag).
     */
    const removeGearboxFromConnection = useCallback((assetId) => {
        const assetToRemove = physicalConnection.find(a =>
            a.assetId === assetId && a.isUnmonitoredGearbox
        );
        if (!assetToRemove) {
            return;
        }

        const newPhysicalConnection = physicalConnection.filter(a => a.assetId !== assetId);
        const newSelectedAssets = selectedAssets.filter(a => a.assetId !== assetId);

        setSelectedAssets(newSelectedAssets);
        setPhysicalConnection(newPhysicalConnection);
        syncToParent(newSelectedAssets, newPhysicalConnection);
    }, [selectedAssets, physicalConnection, syncToParent]);

    return {
        selectedAssets,
        physicalConnection,
        availableAssets,
        findContainer,
        moveToPhysicalConnection,
        removeFromPhysicalConnection,
        reorderPhysicalConnection,
        updateAssetOutputRatio,
        addGearboxToConnection,
        removeGearboxFromConnection
    };
};

/**
 * Custom hook for managing drag and drop operations
 * Handles drag state and events
 * Prevents dragging of assets already connected to powertrains
 */
export const useDragAndDrop = (assetManagement) => {
    const [activeId, setActiveId] = useState(null);
    const [isDraggingConnectedAsset, setIsDraggingConnectedAsset] = useState(false);
    const [isInvalidDropTarget, setIsInvalidDropTarget] = useState(false);

    const {
        selectedAssets,
        physicalConnection,
        findContainer,
        moveToPhysicalConnection,
        removeFromPhysicalConnection,
        reorderPhysicalConnection
    } = assetManagement;

    // Find the active asset being dragged (with ALL properties including outputSpeedRatio)
    const activeAsset = activeId ?
        [...selectedAssets, ...physicalConnection].find(asset => asset.id === activeId) :
        null;

    // Handle drag start
    const handleDragStart = (event) => {
        setActiveId(event.active.id);

        // Check if the dragged asset is connected to a powertrain
        const draggedAsset = [...selectedAssets, ...physicalConnection].find(
            asset => asset.id === event.active.id
        );

        if (draggedAsset?.isConnectedToPowertrain) {
            setIsDraggingConnectedAsset(true);
        }
    };

    // Handle drag over (for visual feedback and cross-container logic)
    const handleDragOver = (event) => {
        const { active, over } = event;

        if (!over) {
            setIsInvalidDropTarget(false);
            return;
        }

        const activeId = active.id;
        const overId = over.id;

        // Check if we're dragging over a different container
        const activeContainer = findContainer(activeId);
        const overContainer = findContainer(overId) || overId;

        // Check if dragging an invalid asset type or connected asset over physical connection
        if (activeContainer === CONTAINER_IDS.SELECTED_ASSETS &&
            (overContainer === CONTAINER_IDS.PHYSICAL_CONNECTION || overContainer === 'physical-connection')) {

            const draggedAsset = selectedAssets.find(a => a.id === activeId);
            if (draggedAsset) {
                const assetType = draggedAsset.assetType || draggedAsset.type;
                const isInvalidType = !isAssetTypeAllowed(assetType);
                const isConnected = draggedAsset.isConnectedToPowertrain;

                setIsInvalidDropTarget(isInvalidType || isConnected);
            } else {
                setIsInvalidDropTarget(false);
            }
        } else {
            setIsInvalidDropTarget(false);
        }
    };

    // Handle drag end
    const handleDragEnd = (event) => {
        const { active, over } = event;

        setActiveId(null);
        setIsDraggingConnectedAsset(false);
        setIsInvalidDropTarget(false);

        if (!over) {
            return;
        }

        const activeId = active.id;
        const overId = over.id;

        // Check if the dragged asset is connected to a powertrain
        const draggedAsset = [...selectedAssets, ...physicalConnection].find(
            asset => asset.id === activeId
        );

        const activeContainer = findContainer(activeId);
        const overContainer = findContainer(overId) || overId;

        // Handle cross-container movement
        if (activeContainer !== overContainer) {
            // Prevent moving connected assets to physical connection
            if (draggedAsset?.isConnectedToPowertrain &&
                activeContainer === CONTAINER_IDS.SELECTED_ASSETS &&
                (overContainer === CONTAINER_IDS.PHYSICAL_CONNECTION || overContainer === 'physical-connection')) {
                // Block the move - asset is already connected to another powertrain
                return;
            }

            // Moving from selected assets to physical connection
            if (activeContainer === CONTAINER_IDS.SELECTED_ASSETS &&
                (overContainer === CONTAINER_IDS.PHYSICAL_CONNECTION || overContainer === 'physical-connection')) {
                moveToPhysicalConnection(activeId);
            }
            // Moving from physical connection back to selected assets
            else if (activeContainer === CONTAINER_IDS.PHYSICAL_CONNECTION &&
                (overContainer === CONTAINER_IDS.SELECTED_ASSETS || overContainer === 'selected-assets')) {
                // Prevent unmonitored gearboxes from being moved back to selected assets
                if (draggedAsset?.isUnmonitoredGearbox) {
                    return;
                }
                removeFromPhysicalConnection(activeId);
            }
        }
        // Handle same-container reordering
        else if (activeContainer === CONTAINER_IDS.PHYSICAL_CONNECTION && overContainer === CONTAINER_IDS.PHYSICAL_CONNECTION) {
            reorderPhysicalConnection(activeId, overId);
        }
    };

    return {
        activeId,
        activeAsset,
        isDraggingConnectedAsset,
        isInvalidDropTarget,
        handleDragStart,
        handleDragOver,
        handleDragEnd
    };
};
